import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Base64 } from 'js-base64';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpParamNames } from 'src/app/core/constantes/http_param-names';
import { ArchivedFolder } from 'src/app/models/server/archived-folder';
import { CommentInput } from 'src/app/models/server/comment-input';
import { Results } from 'src/app/models/server/core/results-T';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { IArchivedFolder } from 'src/app/models/server/interface/archived-folder.interface';
import { TchatRoom } from 'src/app/models/server/tchat-room';
import { FilterInfos } from '../../models/server/core/filter-infos';
import { SortInfos } from '../../models/server/core/sort-infos';
import { ConnectedUsersStateService } from '../state/connected-users-state.service';
import { IFolderViewerService } from './folder-viewer-service.interface';
import { ServiceBase } from './service-base';

@Injectable({
    providedIn: 'root',
})
export class ArchivedFoldersService extends ServiceBase implements IFolderViewerService {
    /**
     * Constructeur
     * */
    constructor(
        private http: HttpClient,
        public override connectedUserStateService: ConnectedUsersStateService
    ) {
        super(connectedUserStateService);
    }

    /**
     * Recupère la liste des dossiers pour une vue spécifique
     * @returns Retourne une liste de dossiers
     */
    public folderList(
        viewId: number,
        from: number,
        size: number,
        sort: SortInfos,
        filters: FilterInfos[],
        generalFilters: FilterInfos[],
        forSelection: boolean = false
    ): Observable<Results<ArchivedFolder>> {
        let params = new HttpParams();
        params = params.set(HttpParamNames.FOR_SELECTION, forSelection);

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                from: from.toString(),
                size: size.toString(),
                sort: !!sort ? Base64.encode(JSON.stringify(sort)) : '',
                filters: !!filters ? Base64.encode(JSON.stringify(filters)) : '',
                generalFilters: !!generalFilters ? Base64.encode(JSON.stringify(generalFilters)) : '',
            }),
            params: params,
        };

        return this.http.get<Results<ArchivedFolder>>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/archives`, httpOptions).pipe(
            map((x) => {
                var folderResult = Results.getCopyFrom<ArchivedFolder>(ArchivedFolder, x);

                // Initialise la proprité expanded à false
                folderResult.value.forEach((fr) => {
                    fr.expanded = false;
                });

                return folderResult;
            })
        );
    }

    /**
     * Exporte la liste des dossiers sous forme de fichier EXCEL.
     * @returns Retourne un fichier EXCEL.
     */
    public export(viewId: number, sort: SortInfos, filters: FilterInfos[], generalFilters: FilterInfos[]): Observable<Blob> {
        let params = new HttpParams();

        const httpOptions = {
            headers: new HttpHeaders({
                sort: !!sort ? Base64.encode(JSON.stringify(sort)) : '',
                filters: !!filters ? Base64.encode(JSON.stringify(filters)) : '',
                generalFilters: !!generalFilters ? Base64.encode(JSON.stringify(generalFilters)) : '',
            }),
            params: params,
            responseType: 'blob' as 'blob',
        };

        return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/archives/export`, httpOptions).pipe(
            map((x) => {
                return x;
            })
        );
    }

    /**
     * Récupère les informations d'un dossier
     * @param folderId: Id du dossier
     */
    public get(viewId: number, folderId: string): Observable<ArchivedFolder> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<IArchivedFolder>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/archives/${folderId}`, httpOptions).pipe(
            map((x) => {
                let folder = ArchivedFolder.from(x);
                return folder;
            })
        );
    }

    /**
     * Recupère le document au format strem
     * @param viewId : Identifiant de la vue.
     * @param folderId Identifiant du dossier
     * @param documentId Identificant du document
     * @param attachmentId Identifiant du fichier
     * @returns retourne un blob
     */
    public download(viewId: number, folderId: string, documentId: string, attachmentId: string): Observable<Blob> {
        const httpOptions = { responseType: 'blob' as 'blob' };

        return this.http
            .get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/archives/${folderId}/documents/${documentId}/attachmentid/${attachmentId}`, httpOptions)
            .pipe(
                map((response) => {
                    return response;
                })
            );
    }

    /**
     * Récupère la conversation.
     * @param folderId Identifiant du dossier
     * @param documentId Identificant du document
     * @param room Nom de la conversation
     * @returns Retourne une conversation.
     */
    public room(folderId: string, documentId: string, room: string): Observable<TchatRoom> {
        throw new Error("Function 'room' not implemented");
    }

    /**
     * Récupère le contenu d'un document contenant les commentaires d'un dossier.
     * @param viewId: Id de la vue
     * @param folderId: Id du dossier
     * @param documentId: Id du document correspondant aux commentaires
     * @param attachmentId Identifiant du fichier
     */
    getCommentContent(viewId: number, folderId: string, documentId: string, fileId: string): Observable<CommentInput[]> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http
            .get<CommentInput[]>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/archives/${folderId}/comments/${documentId}/${fileId}`, httpOptions)
            .pipe(map((x) => x));
    }

    /**
     * Récupère le document contenant les commentaires d'un dossier.
     * @param viewId : Identifiant de la vue
     * @param folderId: Id du dossier
     */
    getComment(viewId: number, folderId: string): Observable<FolderDocument> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<FolderDocument>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/archives/${folderId}/comments-document`, httpOptions).pipe(
            map((x) => {
                return x;
            })
        );
    }
}
